import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import {
  BookkeepingCycle,
  getBookkeepingCycleWorkItemsCompletion,
} from '@/features/jobs/bookkeeping/BookkeepingProgressionInfo'
import { type BookkeepingProgressionStore } from '@/features/jobs/bookkeeping/BookkeepingProgressionStore'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { type Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { CorrectWorkItemViewModel } from '@/features/jobs/details/components/BookkeepingCorrectCycleDialog/CorrectWorkItemViewModel'

/**
 * Corrects the completed work items of a finished bookkeeping cycle.
 */
export class BookkeepingCorrectCycleDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  // A cycle change pairs every work item anew, so its rows start over.
  private readonly _correctedItems = new ViewModelList({
    models: () => this._cycleWorkItems,
    create: ({ workItem, completion }) =>
      new CorrectWorkItemViewModel(workItem, completion),
  })

  readonly modal = new ModalViewModel()

  @observable accessor selectedCycle: BookkeepingCycle = BookkeepingCycle.empty

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _jobId: Id,
    private readonly _bookkeepingProgressionStore: BookkeepingProgressionStore,
    private readonly _returnToList: () => void,
  ) {
    this._lifecycle.manage(this._correctedItems)
  }

  /**
   * Returns the bookkeeping progression info.
   */
  @computed
  get bookkeepingProgressionInfo() {
    return this._bookkeepingProgressionStore.bookkeepingProgressions.get(
      this._jobId,
    )
  }

  /**
   * The corrected items.
   */
  @computed
  get correctedItems() {
    return this._correctedItems.items
  }

  @computed
  private get _cycleWorkItems() {
    const completions = getBookkeepingCycleWorkItemsCompletion(
      this.selectedCycle,
    )
    return (this.bookkeepingProgressionInfo?.workItems ?? []).map(
      (workItem) => ({
        workItem,
        completion: completions.find((ci) => ci.id === workItem.workItemId),
      }),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Validates each corrected item.
   */
  @action.bound
  validate() {
    return (
      this.correctedItems.every(
        (ci) => ci.validation.reset().validate().isValid,
      ) && this.correctedItems.some((ci) => ci.correctAmount >= 0)
    )
  }

  /**
   * Resets each corrected item.
   */
  @action.bound
  reset() {
    this.correctedItems.forEach((ci) => ci.reset())
  }

  /**
   * Sets the cycle to be corrected and shows the dialog.
   */
  @action.bound
  open(selectedCycle: BookkeepingCycle) {
    this.selectedCycle = selectedCycle
    this.modal.show()
  }

  /**
   * Corrects the current cycle.
   */
  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    await this._bookkeepingProgressionStore
      .correctBookkeepingCycle({
        jobId: this._jobId,
        cycleId: this.selectedCycle.id,
        correctedItems: this.correctedItems.map((ci) => ({
          id: ci.workItemId,
          completedCount: ci.correctAmount,
        })),
      })
      .catch(
        this._createAlertErrorHandler(
          'Correcting the bookkeeping cycle failed',
        ),
      )

    this._newAlert()
      .withState('success')
      .saying(`Bookkeeping cycle ${this.selectedCycle.number} corrected.`)
      .show()
      .autoDismiss()

    this.reset()
    this.modal.close()
    this._returnToList()
  })
}
